'use client'

import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'

import { sanitizeOrderProductBannerHtml } from '@/_lib/dom-purify.utils'
import { sanitizeHtml } from '@/_lib/sanitize-html.client'
import { cn } from '@/_lib/utils'
import { IProductListBanner } from '@/_types'
import { SECTION_THEME_ENUM } from '@/_types/base'
import { processLink } from '@/_utils/link.utils'

import { ZOrderProductCard } from '../z-order-product-card'

/**
 * Optional CTA link rendered under banner HTML when both link and label are non-empty.
 *
 * @param props - Button props from {@link IProductListBanner}.
 * @param props.buttonLink - Target URL; whitespace-only is treated as missing.
 * @param props.buttonText - Visible label; whitespace-only is treated as missing.
 * @returns A `Link` wrapped in a margin container, or `null` if either value is missing.
 */
function BannerButton({ buttonLink, buttonText }: { buttonLink?: string; buttonText?: string }) {
	const isButtonValid = useMemo(() => {
		return buttonLink && buttonLink.trim() && buttonText && buttonText.trim()
	}, [buttonLink, buttonText])

	if (!isButtonValid) {
		return null
	}

	const buttonContent = (
		<>
			<span>{buttonText}</span>
			<span aria-hidden='true'>
				<span>
					<svg className='sico' aria-hidden='true'>
						<use href='/assets/layout/icons.svg#s_ar2_r' />
					</svg>
				</span>
				<span>
					<svg className='sico' aria-hidden='true'>
						<use href='/assets/layout/icons.svg#s_ar2_r' />
					</svg>
				</span>
			</span>
		</>
	)

	const buttonClassName =
		'btn_basic type4 type4-light hover:cursor-pointer whitespace-nowrap inline-flex items-center [&>span]:py-0! [&>span]:pl-1.5! py-0.5!'

	// Only render if both link and text are provided
	if (!buttonLink || !buttonLink.trim() || !buttonText || !buttonText.trim()) {
		return null
	}

	return (
		<div className='mt-4'>
			<Link {...processLink(buttonLink)} className={buttonClassName}>
				{buttonContent}
			</Link>
		</div>
	)
}

/**
 * Renders a product-list banner (title + rich text body) inside {@link ZOrderProductCard}.
 *
 * @param props - Banner data from the API or the default fallback in the select-product step.
 * @param props.banner - `IProductListBanner` with HTML strings for `title` and `text`.
 */
export const ABannerCard = ({
	banner,
	sectionTheme = null
}: {
	banner: IProductListBanner
	sectionTheme?: SECTION_THEME_ENUM | null
}) => {
	const [isMounted, setIsMounted] = useState(false)

	useEffect(() => {
		setIsMounted(true)
	}, [])

	const bannerSanitizeConfig = { ADD_ATTR: ['target', 'rel', 'class'] }
	const processedTitle = useMemo(
		() => sanitizeOrderProductBannerHtml(banner.title ?? ''),
		[banner.title]
	)
	const processedText = useMemo(
		() => sanitizeOrderProductBannerHtml(banner.text ?? ''),
		[banner.text]
	)
	const safeTitle = isMounted
		? sanitizeHtml(processedTitle, bannerSanitizeConfig)
		: processedTitle
	const safeText = isMounted ? sanitizeHtml(processedText, bannerSanitizeConfig) : processedText

	return (
		<ZOrderProductCard split sectionTheme={sectionTheme}>
			<div>
				<div
					className={cn(
						'mb-1',
						'text-medante-purple-dark text-base/5 md:text-xl font-semibold lg:font-medium',
						'*:text-medante-purple-dark *:md:text-xl *:font-semibold *:lg:font-medium'
					)}
					dangerouslySetInnerHTML={{ __html: safeTitle }}
				/>

				<div
					className='*:text-sm *:leading-normal *:text-medante-purple-dark/90 [&_a]:text-medante-purple-dark [&_a]:underline'
					dangerouslySetInnerHTML={{ __html: safeText }}
				/>
			</div>

			<BannerButton buttonLink={banner.buttonLink} buttonText={banner.buttonText} />
		</ZOrderProductCard>
	)
}
